import { useMutation } from "react-query";
import { Card, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { AxiosError } from "axios";

import { useToast } from "src/context/ToastContext";
import { CriteriaEntityPostRequest, CriteriaEntityPutRequest } from "src/entities/CriteriaEntity";
import CriteriaService from "src/services/CriteriaService";
import Navigation from "./Navigation";

const criteriaService = new CriteriaService();
const toastTitle = "Critério";

type FormData = { title: string; text: string };

type StepTitleProps = {
	criteriaId?: number;
	defaultValues: FormData;
	backLabel: string;
	nextLabel: string;
	isLoading?: boolean;
	onBack: () => void;
	onComplete: (savedData: FormData, newId?: number) => void;
};

export default function StepTitle({ criteriaId, defaultValues, backLabel, nextLabel, isLoading, onBack, onComplete }: StepTitleProps) {
	const { handleToast } = useToast();
	const { register, handleSubmit } = useForm<FormData>({ defaultValues });

	const mutationPost = useMutation(
		(data: CriteriaEntityPostRequest) => criteriaService.post(data),
		{
			onError: (err: AxiosError) => {
				handleToast(toastTitle, `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	const mutationPut = useMutation(
		(data: CriteriaEntityPutRequest) => criteriaService.put(criteriaId!, data),
		{
			onError: (err: AxiosError) => {
				handleToast(toastTitle, `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	function handleNext(values: FormData) {
		if (criteriaId !== undefined) {
			if (values.title === defaultValues.title && values.text === defaultValues.text) {
				onComplete(values);
				return;
			}
			mutationPut.mutate({ title: values.title, text: values.text }, {
				onSuccess: () => onComplete(values),
			});
		} else {
			mutationPost.mutate({ title: values.title, text: values.text }, {
				onSuccess: (resp) => {
					handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
					onComplete(values, resp.data.id);
				},
			});
		}
	}

	const saving = criteriaId !== undefined ? mutationPut.isLoading : mutationPost.isLoading;

	return (
		<>
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Critério
				</Card.Header>
				<Card.Body>
					{isLoading ? (
						<>
							<Form.Group className="mb-3">
								<div className="skeleton skeleton-text skeleton-text-title mb-2" style={{ height: 16, width: "10%" }} />
								<div className="skeleton skeleton-button" style={{ height: 38 }} />
							</Form.Group>
							<Form.Group className="mb-3">
								<div className="skeleton skeleton-text skeleton-text-title mb-2" style={{ height: 16, width: "10%" }} />
								<div className="skeleton skeleton-button" style={{ height: 120 }} />
							</Form.Group>
						</>
					) : (
						<>
						<Form.Group className="mb-3" controlId="titulo">
							<Form.Label className="text-muted"><small>Título *</small></Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("title", { required: true })} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="texto">
							<Form.Label className="text-muted"><small>Texto *</small></Form.Label>
							<Form.Control as="textarea" rows={5} placeholder="Informe aqui" {...register("text", { required: true })} />
						</Form.Group>
						</>
					)}
				</Card.Body>
			</Card>
			<Navigation
				backLabel={backLabel}
				nextLabel={nextLabel}
				saving={saving}
				onBack={onBack}
				onNext={handleSubmit(handleNext)}
			/>
		</>
	);
}
